// Patient flow: the call slip, the ward check-out, the holding bay, and the
// board.
//
// Holding-bay check-in is the one hard stop between the ward and the theatre
// door. It is refused unless consent is obtained or waived AND the four pre-op
// checklist items are ticked — and the reasons shown here are the server's own,
// so the screen cannot say "ready" when the endpoint would say otherwise.
//
// The board moves forward only. There is no un-check-in: a patient sent back to
// the ward is a postponed case, not a rewound board.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime } from "../../../../utils/istDateTime";
import {
  fetchCallSlips,
  fetchHoldingCheckin,
  fetchPreopChecklist,
  fetchVitals,
  holdingCheckin,
  issueCallSlip,
  moveTracking,
  recordVitals,
  updatePorterTask,
  wardCheckout,
} from "../../api/otApi";
import {
  Field,
  FieldGrid,
  GateNotice,
  OtSelect,
  TrackingChip,
  errorMessage,
  report,
} from "../common";
import { ChecklistCard } from "./PreopPanel";
import { TRACKING_LABELS } from "../../constants";
import { printCallSlip } from "../../utils/otPdf";

export default function FlowPanel({ otCase, onChanged }) {
  const [slips, setSlips] = useState([]);
  const [holding, setHolding] = useState(null);
  // The same pre-op checklist the Pre-op tab shows. Loaded here so the
  // nurse who is blocked at check-in can clear the blocker without
  // leaving the screen.
  const [checklist, setChecklist] = useState(null);
  const [vitals, setVitals] = useState([]);
  const [error, setError] = useState("");
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  const [slipBed, setSlipBed] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [s, h, v, c] = await Promise.all([
        fetchCallSlips(otCase.id),
        fetchHoldingCheckin(otCase.id),
        fetchVitals(otCase.id, { context: "HOLDING" }),
        fetchPreopChecklist(otCase.id),
      ]);
      setSlips(s || []);
      setHolding(h);
      setVitals(v || []);
      setChecklist(c);
    } catch (err) {
      setError(errorMessage(err, "Could not load the patient flow"));
    }
  }, [otCase.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Ticking a box here changes the gate, which is what the check-in button
  // reads — so both are patched from the one response rather than
  // re-fetching the panel.
  const patchChecklist = (type, payload) => {
    setChecklist((current) => ({
      ...current,
      [type === "PREOP" ? "preop" : "daycare"]: payload.checklist,
      gate: payload.gate,
    }));
    setHolding((current) => ({ ...current, gate: payload.gate }));
  };

  const refreshAll = async () => {
    await load();
    onChanged();
  };

  if (error) return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;

  const next = otCase.allowed_tracking_transitions || [];
  const latestSlip = slips[0];

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <IpdSectionCard title="Where the patient is">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TrackingChip status={otCase.tracking_status} />
          {next.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
              {otCase.tracking_status
                ? "The board has nothing further for this case."
                : "The board starts when the case is called for."}
            </Typography>
          ) : (
            next.map((to) => (
              <Button
                key={to}
                size="small"
                variant="outlined"
                onClick={async () => {
                  if (report(await moveTracking(otCase.id, to))) refreshAll();
                }}
              >
                Move to {TRACKING_LABELS[to] || to}
              </Button>
            ))
          )}
        </Box>
        <Typography sx={{ mt: 1.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          The board only moves forward, one step at a time. Sending a patient back to the ward is
          a postponement of the case, not a change here.
        </Typography>
      </IpdSectionCard>

      <IpdSectionCard
        title="Call slip"
        subtitle="Issuing a slip raises the porter task."
        actions={
          <Button
            size="small"
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => setSlipOpen(true)}
          >
            Issue call slip
          </Button>
        }
      >
        {!latestSlip ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            No call slip issued yet.
          </Typography>
        ) : (
          <>
            <FieldGrid columns={4}>
              <Field label="Patient" value={latestSlip.patient_name} />
              <Field label="MRN" value={latestSlip.health_record_id} />
              <Field label="Ward / bed" value={`${latestSlip.ward_name || "—"} / ${latestSlip.bed_label || "—"}`} />
              <Field label="Theatre" value={latestSlip.operating_room_name} />
              <Field label="Procedure" value={latestSlip.procedure_name} span={2} />
              <Field label="Scheduled" value={formatIstDateTime(latestSlip.scheduled_start)} />
              <Field label="Issued" value={`${formatIstDateTime(latestSlip.issued_at)} by ${latestSlip.issued_by_name || "—"}`} />
            </FieldGrid>
            <Box sx={{ mt: 2, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                Porter task: {latestSlip.porter_task_status}
              </Typography>
              {["ACKNOWLEDGED", "COMPLETED", "CANCELLED"].map((s) => (
                <Button
                  key={s}
                  size="small"
                  disabled={latestSlip.porter_task_status === s}
                  onClick={async () => {
                    if (report(await updatePorterTask(latestSlip.id, { porter_task_status: s })))
                      refreshAll();
                  }}
                >
                  {s.toLowerCase()}
                </Button>
              ))}
              <Button size="small" onClick={() => printCallSlip(latestSlip, otCase)}>
                Print
              </Button>
            </Box>
          </>
        )}
      </IpdSectionCard>

      <IpdSectionCard
        title="Ward check-out"
        subtitle="An inpatient's bed is held for their return; a daycare patient's is released."
        actions={
          <Button
            size="small"
            variant="contained"
            disabled={!otCase.admission_id}
            onClick={async () => {
              if (report(await wardCheckout(otCase.id, {}))) refreshAll();
            }}
          >
            Check out of the ward
          </Button>
        }
      >
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
          {otCase.admission_id
            ? `Linked to admission #${otCase.admission_id}. The bed follows the scheduling type unless you say otherwise.`
            : "This case has no admission, so there is no ward bed to check out of."}
        </Typography>
      </IpdSectionCard>

      <IpdSectionCard
        title="Holding bay"
        subtitle="The last point at which an unprepared patient can be turned back."
      >
        <Box sx={{ mb: 2 }}>
          <GateNotice
            gate={holding?.gate}
            readyMessage="Consent and the four safety items are done — the patient may be checked in."
          />
        </Box>

        {/* The same checklist record as the Pre-op tab, rendered here
            because this is where the nurse is stopped. Sending them to
            another tab to tick the box that is blocking them, then back
            again, is the whole friction. Shown only while it blocks:
            once the gate is green the notice above says everything. */}
        {holding?.gate && !holding.gate.satisfied && checklist?.preop && (
          <Box sx={{ mb: 2.5 }}>
            <ChecklistCard
              otCase={otCase}
              checklist={checklist}
              onPatch={patchChecklist}
              bare
            />
          </Box>
        )}

        {holding?.checkin ? (
          <FieldGrid columns={4}>
            <Field label="Arrived" value={formatIstDateTime(holding.checkin.arrived_at)} />
            <Field label="Identity verified" value={holding.checkin.identity_verified ? "Yes" : "No"} />
            <Field label="Method" value={holding.checkin.verification_method} />
            <Field
              label="Blood"
              value={
                holding.checkin.blood_requested
                  ? holding.checkin.blood_available
                    ? "Requested and available"
                    : "Requested — NOT available"
                  : "Not requested"
              }
            />
            <Field label="Checked in by" value={holding.checkin.checked_in_by_name} span={2} />
            <Field label="Notes" value={holding.checkin.notes} span={2} />
          </FieldGrid>
        ) : (
          <CheckinForm caseId={otCase.id} disabled={!holding?.gate?.satisfied} onDone={refreshAll} />
        )}
      </IpdSectionCard>

      <IpdSectionCard
        title="Holding-bay observations"
        actions={
          <Button size="small" variant="contained" onClick={() => setVitalsOpen(true)}>
            Record observations
          </Button>
        }
      >
        {vitals.length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            Nothing recorded yet.
          </Typography>
        ) : (
          vitals.map((v) => (
            <Typography
              key={v.id}
              sx={{ fontSize: 13, mb: 0.5, borderBottom: `1px solid ${IPD_COLORS.border}`, pb: 0.5 }}
            >
              {formatIstDateTime(v.recorded_at)} — BP {v.systolic_bp ?? "—"}/{v.diastolic_bp ?? "—"},
              HR {v.heart_rate ?? "—"}, RR {v.respiratory_rate ?? "—"}, SpO₂ {v.spo2 ?? "—"}%, temp{" "}
              {v.temperature ?? "—"} {v.consciousness ? `· ${v.consciousness}` : ""}
              {v.recorded_by_name ? ` · ${v.recorded_by_name}` : ""}
            </Typography>
          ))
        )}
      </IpdSectionCard>

      <Dialog open={slipOpen} onClose={() => setSlipOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Issue a call slip</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
            The slip snapshots the patient, ward, procedure and theatre as they are now, and
            raises the porter task.
          </Typography>
          {/* The bed is the admission's. Asking a theatre clerk to type it
              from memory is how the porter ends up at the wrong bedside; the
              slip still SNAPSHOTS it, because the paper in the ward's hand has
              to match what was sent, but the value snapshotted is now the real
              one. Only a patient with no admission is asked. */}
          {otCase.location?.from_admission ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: IPD_COLORS.pageBg,
                border: `1px solid ${IPD_COLORS.border}`,
              }}
            >
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                {otCase.location.ward_name || "Ward not recorded"}
                {otCase.location.bed_label
                  ? ` \u00b7 bed ${otCase.location.bed_label}`
                  : " \u00b7 no bed assigned"}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary }}>
                from admission #{otCase.location.admission_id}
              </Typography>
            </Box>
          ) : (
            <IpdFormField label="Which bed is the patient in? (optional)">
              <IpdTextField
                fullWidth
                autoFocus
                value={slipBed}
                onChange={(e) => setSlipBed(e.target.value)}
                placeholder="e.g. W1-B12"
              />
            </IpdFormField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlipOpen(false)}>Cancel</Button>
          <IpdButton
            onClick={async () => {
              if (report(await issueCallSlip(otCase.id, { bed_label: slipBed }))) {
                setSlipOpen(false);
                setSlipBed("");
                refreshAll();
              }
            }}
          >
            Issue
          </IpdButton>
        </DialogActions>
      </Dialog>

      <VitalsDialog
        open={vitalsOpen}
        caseId={otCase.id}
        context="HOLDING"
        onClose={() => setVitalsOpen(false)}
        onDone={() => {
          setVitalsOpen(false);
          refreshAll();
        }}
      />
    </Box>
  );
}

function CheckinForm({ caseId, disabled, onDone }) {
  const [form, setForm] = useState({
    identity_verified: true,
    verification_method: "BARCODE",
    blood_requested: false,
    blood_available: false,
    notes: "",
  });

  return (
    <Box>
      <FieldGrid columns={3}>
        <IpdFormField label="Identity verified">
          <OtSelect
            value={form.identity_verified ? "yes" : "no"}
            onChange={(e) =>
              setForm((f) => ({ ...f, identity_verified: e.target.value === "yes" }))
            }
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />
        </IpdFormField>
        <IpdFormField label="How">
          <OtSelect
            value={form.verification_method}
            onChange={(e) => setForm((f) => ({ ...f, verification_method: e.target.value }))}
            options={[
              { value: "BARCODE", label: "Barcode" },
              { value: "MANUAL", label: "Manual" },
            ]}
          />
        </IpdFormField>
        <IpdFormField label="Blood requested">
          <OtSelect
            value={form.blood_requested ? "yes" : "no"}
            onChange={(e) => setForm((f) => ({ ...f, blood_requested: e.target.value === "yes" }))}
            options={[
              { value: "no", label: "No" },
              { value: "yes", label: "Yes" },
            ]}
          />
        </IpdFormField>
        {form.blood_requested && (
          <IpdFormField label="Blood available">
            <OtSelect
              value={form.blood_available ? "yes" : "no"}
              onChange={(e) => setForm((f) => ({ ...f, blood_available: e.target.value === "yes" }))}
              options={[
                { value: "no", label: "Not yet" },
                { value: "yes", label: "Confirmed available" },
              ]}
            />
          </IpdFormField>
        )}
      </FieldGrid>
      <Box sx={{ mt: 2 }}>
        <IpdFormField label="Notes">
          <IpdTextField
            fullWidth
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </IpdFormField>
      </Box>
      <Box sx={{ mt: 2 }}>
        <IpdButton
          disabled={disabled}
          onClick={async () => {
            if (report(await holdingCheckin(caseId, form))) onDone();
          }}
        >
          Check the patient in
        </IpdButton>
        {disabled && (
          <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
            Clear the blockers above first.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Shared by the holding bay and by recovery — an observation set is the same
 *  shape wherever it is taken, which is why one table backs both. */
export function VitalsDialog({ open, caseId, context, onClose, onDone }) {
  const [form, setForm] = useState({});

  const fields = [
    ["systolic_bp", "Systolic BP"],
    ["diastolic_bp", "Diastolic BP"],
    ["heart_rate", "Heart rate"],
    ["respiratory_rate", "Respiratory rate"],
    ["spo2", "SpO₂ %"],
    ["temperature", "Temperature"],
    ["pain_score", "Pain score"],
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Record observations</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
          {fields.map(([key, label]) => (
            <IpdFormField key={key} label={label}>
              <IpdTextField
                fullWidth
                value={form[key] ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              />
            </IpdFormField>
          ))}
          <IpdFormField label="Consciousness">
            <IpdTextField
              fullWidth
              value={form.consciousness ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, consciousness: e.target.value }))}
            />
          </IpdFormField>
        </Box>
        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Notes">
            <IpdTextField
              fullWidth
              value={form.notes ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </IpdFormField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton
          onClick={async () => {
            if (report(await recordVitals(caseId, { ...form, context }))) {
              setForm({});
              onDone();
            }
          }}
        >
          Record
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}
